import { ToggleButtonSet, type ToggleOption, theme } from '@socrait/react';
import { StyleSheet } from 'react-native';

import { getMeetingTimeRanges, getMeetingTitles } from '~/pages/review/utils/meetingSelection';

type MeetingSelectListProps = {
  meetings: { meetingId: string; startedAt: string; endedAt: string }[];
  selectedMeetingId?: string;
  onSelect: (meetingId: string) => void;
  timeZone?: string;
};

/** Meeting switcher for the review left sidebar. Callers should only render this when
 *  `meetings.length > 1` — single-meeting days keep the plain layout with no list. */
export function MeetingSelectList({ meetings, selectedMeetingId, onSelect, timeZone }: MeetingSelectListProps) {
  const titles = getMeetingTitles(meetings, timeZone);
  const timeRanges = getMeetingTimeRanges(meetings, timeZone);
  const sorted = [...meetings].sort((a, b) => a.startedAt.localeCompare(b.startedAt));
  const options: ToggleOption[] = sorted.map((meeting) => ({
    label: titles.get(meeting.meetingId) ?? meeting.meetingId,
    subtitle: timeRanges.get(meeting.meetingId),
    value: meeting.meetingId,
  }));

  return (
    <ToggleButtonSet
      options={options}
      selectedValue={selectedMeetingId}
      onSelect={onSelect}
      fullWidth
      activeColor={theme.colors.primaryPurple}
      containerStyle={styles.container}
      optionStyle={styles.option}
      accessibilityLabel="Select a meeting"
    />
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: theme.borderRadius.large,
  },
  option: {
    minHeight: 60,
  },
});
